/**
 * Wall-clock waits: `wait(ms)` and `waitFor(condition)`.
 *
 * A leaf module — it imports nothing from the environment — so the environment's game-clock waits
 * (`environment-waits.ts`) and the test helpers (`test-helpers.ts`, which re-exports both) share one
 * `waitFor` without an import cycle.
 */

/** Resolves after `ms` milliseconds of wall time. */
export const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// `wait(ms)` is a deliberately-thin re-export of the same `setTimeout` promise
// used internally. It exists for one reason: generated test code (and human-
// authored test code that pre-dates `waitFor`) expects an importable `wait`
// helper. Without this export, common patterns like
//
//   import { wait } from '@gotti/test-utils';
//   await wait(100);
//
// would resolve `wait` to `undefined` and crash with `wait is not a function`
// the first time the test runs. The crash happens during sandbox runtime
// boot — not in a test runner — because the AI-generated tests share their
// module cache with the live sandbox runtime.
//
// PREFER `waitFor(condition)` for anything beyond a fixed delay — sleep-based
// waits are flaky and harder to reason about. Use `wait(ms)` only when you
// genuinely just need N milliseconds to pass (e.g. waiting for a debounce).
export const wait = delay;

/** How long `waitFor` polls, how often, and what its failure says it was waiting for. */
export interface WaitForOptions {
  /** Max time to wait in ms. Default: 5000. */
  timeout?: number;
  /** Polling interval in ms. Default: 16 (~1 frame at 60fps). */
  interval?: number;
  /** What was awaited, for the failure. A FUNCTION is evaluated only at timeout, so it can embed live state without paying for the string on every green poll. */
  message?: string | (() => string);
}

/**
 * Poll until `condition()` returns a truthy value, then return it.
 *
 * Replaces fragile `await new Promise(r => setTimeout(r, 100))` patterns.
 * Does NOT require the active environment — works for any async polling.
 * The condition runs at least once, and once more after the timeout before it gives up. A throw
 * reads as "not ready yet"; when the LAST evaluation threw, its message is appended to the failure.
 *
 * ```ts
 * await waitFor(() => manager.players.size === 2);
 * await waitFor(() => canvas.getContext('2d')!.getImageData(0,0,1,1).data[3] > 0, {
 *   timeout: 3000,
 *   message: () => `the canvas to draw (players: ${manager.players.size})`,
 * });
 * ```
 */
export async function waitFor<T>(
  condition: () => T | Promise<T>,
  options?: WaitForOptions,
): Promise<NonNullable<T>> {
  const timeout = options?.timeout ?? 5000;
  const interval = options?.interval ?? 16;
  const deadline = Date.now() + timeout;
  let lastError: unknown;
  for (;;) {
    // Only the LAST evaluation's reason counts: its throw, or nothing when it returned falsy.
    lastError = undefined;
    try {
      const result = await condition();
      if (result) return result as NonNullable<T>;
    } catch (err) {
      lastError = err;
    }
    if (Date.now() >= deadline) {
      const message = failureMessage(options?.message, 'waitFor() timed out — condition never became truthy');
      throw new Error(`[test-utils] ${message} (after ${timeout}ms)${describeLastError(lastError)}`);
    }
    await delay(interval);
  }
}

/**
 * What a wait's failure says it waited for: `message` — a function is evaluated now, at the failure — else `fallback`.
 * A message function that throws is named in its place, `(its message threw: <reason>)`, as `expectRendering` does:
 * the failure's own evidence (how long it waited, the last error) is never lost to it.
 */
export const failureMessage = (message: WaitForOptions['message'], fallback: string): string => {
  if (typeof message !== 'function') return message ?? fallback;
  try {
    return String(message());
  } catch (thrown) {
    return `(its message threw: ${thrown instanceof Error ? thrown.message : String(thrown)})`;
  }
};

/** `: <reason>` for a failure message when the last evaluation threw, else nothing. */
export const describeLastError = (lastError: unknown): string =>
  lastError === undefined ? '' : `: ${lastError instanceof Error ? lastError.message : String(lastError)}`;
